import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {StateMatrix} from "./StateMatrix";
import {DirtyWildsGameConfig} from "../game/DirtyWildsGameConfig";
import {Timeline} from "gsap";
import {DirtyWildsStateReelSymbol} from "../game/symbols/DirtyWildsStateReelSymbol";

/**
 * Class description
 *
 * Created: 2018-05-18
 * @author jonas
 */

export interface IAdjacentBetLineWinData extends IBetLineWinData {
    winPosition:boolean[];
    lineStart:string;
}

export class SymbolUtil {


    public static getSymbolsAtPositions(positions:[number, number][]):StateReelSymbol[] {
        const symbols:StateReelSymbol[] = [];
        for(let position of positions) {
            const symbol:StateReelSymbol | null = Reels.getSymbol(position[0], position[1]);
            if(symbol) {
                symbols.push(symbol);
            }
        }
        return symbols;
    }

    public static createSymbolStateMatrixFromBetWinsBeforeSpin(betWinsData:IBetLineWinData[]):StateMatrix<string> {
        let states:StateMatrix<string> = new StateMatrix<string>(5, 3, [0, 2], DirtyWildsGameConfig.instance.SYMBOL_STATES.noWin.keyword);

        for(let i:number = 0; i < betWinsData.length; i++) {
            let betLineWinData:IAdjacentBetLineWinData = <IAdjacentBetLineWinData>betWinsData[i];

            for(let j:number = 0; j < betLineWinData.winPosition.length; j++) {
                if(betLineWinData.winPosition[j]) {
                    states.setStateAt(j, betLineWinData.betLine[j], DirtyWildsGameConfig.instance.SYMBOL_STATES.win.keyword);
                }
            }
        }
        return states;
    }

    public static createSymbolStateMatrixFromBetWins(betWinsData:IBetLineWinData[]):StateMatrix<string> {
        let states:StateMatrix<string> = new StateMatrix<string>(5, 3, [0, 2], DirtyWildsGameConfig.instance.SYMBOL_STATES.noWin.keyword);

        for(let i:number = 0; i < betWinsData.length; i++) {
            let betLineWinData:IAdjacentBetLineWinData = <IAdjacentBetLineWinData>betWinsData[i];

            for(let j:number = 0; j < betLineWinData.winPosition.length; j++) {
                if(betLineWinData.winPosition[j]) {
                    states.setStateAt(j, betLineWinData.betLine[j], DirtyWildsGameConfig.instance.SYMBOL_STATES.win.keyword);
                }
            }
        }
        return states;
    }

    public static createSymbolStateMatrixFromBetWinsFromDirection(betWinsData:IBetLineWinData[], direction:string):StateMatrix<string> {
        let states:StateMatrix<string> = new StateMatrix<string>(5, 3, [0, 2], DirtyWildsGameConfig.instance.SYMBOL_STATES.noWin.keyword);

        for(let i:number = 0; i < betWinsData.length; i++) {
            let betLineWinData:IAdjacentBetLineWinData = <IAdjacentBetLineWinData>betWinsData[i];

            for(let j:number = 0; j < betLineWinData.winPosition.length; j++) {
                if(betLineWinData.winPosition[j] && betLineWinData.lineStart == direction) {
                    states.setStateAt(j, betLineWinData.betLine[j], DirtyWildsGameConfig.instance.SYMBOL_STATES.win.keyword);
                }
            }
        }
        return states;
    }

    public static getSymbolsWithStatePerReel(symbolStates:StateMatrix<string>, state:string):StateReelSymbol[][] {
        const symbols:StateReelSymbol[][] = [];
        for(let reelIndex:number = 0; reelIndex < 5; reelIndex++) {
            const symbolsInReel:StateReelSymbol[] = [];
            const coordinates:[number, number][] = symbolStates.getCoordinatesWithStateInReel(reelIndex, state);
            for(let i:number = 0; i < coordinates.length; i++) {
                const reelIndex:number = coordinates[i][0];
                const symbolIndex:number = coordinates[i][1];
                const symbol:StateReelSymbol | null = <StateReelSymbol>Reels.getSymbol(reelIndex, symbolIndex);
                if(symbol != null) {
                    symbolsInReel.push(symbol);
                }
            }
            symbols.push(symbolsInReel);

        }
        return symbols;
    }

    public static getSymbolsWithStatePerReelForInitialWin(symbolStates:StateMatrix<string>, state:string):DirtyWildsStateReelSymbol[][] {
        const symbols:DirtyWildsStateReelSymbol[][] = [];
        for(let reelIndex:number = 0; reelIndex < 5; reelIndex++) {
            const symbolsInReel:DirtyWildsStateReelSymbol[] = [];
            const coordinates:[number, number][] = symbolStates.getCoordinatesWithStateInReel(reelIndex, state);
            for(let i:number = 0; i < coordinates.length; i++) {
                const reelIndex:number = coordinates[i][0];
                const symbolIndex:number = coordinates[i][1];
                const symbol:DirtyWildsStateReelSymbol | null = <DirtyWildsStateReelSymbol>Reels.getSymbol(reelIndex, symbolIndex);
                if(symbol != null) {
                    symbolsInReel.push(symbol);
                }
            }
            symbols.push(symbolsInReel);

        }
        return symbols;
    }

    public static getSymbolsWithState(symbolStates:StateMatrix<string>, state:string):DirtyWildsStateReelSymbol[] {
        const symbols:DirtyWildsStateReelSymbol[] = [];
        const coordinates:[number, number][] = symbolStates.getAllCoordinatesWithState(state);
        for(let i:number = 0; i < coordinates.length; i++) {
            const reelIndex:number = coordinates[i][0];
            const symbolIndex:number = coordinates[i][1];
            const symbol:DirtyWildsStateReelSymbol | null = <DirtyWildsStateReelSymbol>Reels.getSymbol(reelIndex, symbolIndex);
            if(symbol != null) {
                symbols.push(symbol);
            }
        }
        return symbols;
    }

    public static getDirtySymbolStateAnimations(symbols:DirtyWildsStateReelSymbol[], newState:ISymbolStateConfig, fadeDuration:number = 0.001):Timeline[] {
        const animations:Timeline[] = [];

        for(let symbol of symbols) {
            if(!(symbol.controller._isRandomSyncFeatureTriggered && (symbol.controller.reelId === 0 || symbol.controller.reelId === 4))) {
                let animation: Timeline | null = symbol.changeState({
                    state: newState,
                    fadeInDuration: fadeDuration
                }, {
                    fadeOutDelay: fadeDuration,
                    fadeOutDuration: 0
                });


                if (animation != null) {
                    animations.push(animation);
                }
            }
        }

        return animations;
    }

    public static getSymbolStateAnimations(symbols:StateReelSymbol[], newState:ISymbolStateConfig, fadeDuration:number = 0.001):Timeline[] {
        const animations:Timeline[] = [];

        for(let symbol of symbols) {
            let animation:Timeline | null = symbol.changeState({
                state : newState,
                fadeInDuration : fadeDuration
            }, {
                fadeOutDelay : fadeDuration,
                fadeOutDuration : 0
            });

            if(animation != null) {
                animations.push(animation);
            }
        }

        return animations;
    }

    public static getSymbolStateAnimationsForInitialWin(symbols:DirtyWildsStateReelSymbol[], newState:ISymbolStateConfig, fadeDuration:number = 0.001, winBothWays:boolean = false):Timeline[] {
        const animations:Timeline[] = [];

        for(let symbol of symbols) {
            symbol.speedUpWinAnim = winBothWays;
            let animation:Timeline | null = symbol.changeState({
                state : newState,
                fadeInDuration : fadeDuration
            }, {
                fadeOutDelay : fadeDuration,
                fadeOutDuration : 0
            });

            if(animation != null) {
                animations.push(animation);
            }
        }

        return animations;
    }

}